import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import databaseAPI from '../api/databaseAPI';
import '../styles/ProductDetail.css';
import { authFetch, authHeaders } from '../api/auth';

const KeyValue = ({ label, value }) => (
  <div className="kv">
    <div className="kv-label">{label}</div>
    <div className="kv-value">{value ?? '—'}</div>
  </div>
);

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [metafields, setMetafields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({});
  const [definitions, setDefinitions] = useState([]);
  // Rich text editor (WYSIWYG) mode only; HTML textarea removed

  const base = databaseAPI.baseURL || '';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        // Fast path: DB-first product payload already includes metafields.
        const pRes = await authFetch(`${base}/products/${id}/`);
        if (!pRes.ok) throw new Error(`Product HTTP ${pRes.status}`);
        const pData = await pRes.json();
        const prod = pData.product || pData;
        if (!cancelled) {
          setProduct(prod);
          setMetafields(Array.isArray(prod?.metafields) ? prod.metafields : []);
        }

        // Non-blocking enrichments; UI is already visible.
        const [mRes, dRes] = await Promise.allSettled([
          fetch(`${base}/products/${id}/metafields/`),
          fetch(`${base}/metafield-definitions/?owner_type=PRODUCT`)
        ]);

        if (!cancelled && mRes.status === 'fulfilled' && mRes.value.ok) {
          const mData = await mRes.value.json();
          const mfs = mData.metafields || mData || [];
          setMetafields(Array.isArray(mfs) ? mfs : []);
        }
        if (!cancelled && dRes.status === 'fulfilled' && dRes.value.ok) {
          const defs = (await dRes.value.json()).definitions || [];
          setDefinitions(defs);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id, base]);

  const cover = useMemo(() => product?.image?.src || product?.images?.[0]?.src || '', [product]);

  const selectImage = (img) => {
    setProduct(p => ({ ...p, image: img }));
    setEdit(e => ({ ...e, image_id: img.id }));
  };

  const saveChanges = async () => {
    if (!product) return;
    setSaving(true);
    setError('');
    try {
      const body = {
        title: edit.title ?? product.title,
        body_html: edit.body_html ?? product.body_html,
        vendor: edit.vendor ?? product.vendor,
        product_type: edit.product_type ?? product.product_type,
        tags: edit.tags ?? product.tags,
        status: edit.status ?? product.status,
        image_id: edit.image_id,
      };
      const res = await authFetch(`${base}/products/${id}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      const data = await res.json();
      setProduct(data.product || product);
      setEdit({});
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="pd-container"><div className="pd-loading">Loading product…</div></div>;
  }
  if (error) {
    return <div className="pd-container"><div className="pd-error">{error}</div></div>;
  }
  if (!product) {
    return <div className="pd-container"><div className="pd-error">Product not found</div></div>;
  }

  const preorderMf = metafields.find(m => m.namespace === 'custom' && m.key === 'preorder_status');
  const preorderOn = String(preorderMf?.value).toLowerCase() === 'true' || preorderMf?.value === true;

  const togglePreorder = async () => {
    try {
      if (preorderMf?.id) {
        // Shopify often disallows changing metafield type once created
        // So only send value, and respect existing type
        const desired = !preorderOn;
        const valueToSend = (preorderMf.type && preorderMf.type !== 'boolean') ? String(desired) : desired;
        const res = await authFetch(`${base}/metafields/${preorderMf.id}/`, {
          method:'PUT',
          headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ value: valueToSend })
        });
        if (!res.ok) throw new Error('Failed to update preorder');
      } else {
        const body = { namespace:'custom', key:'preorder_status', type:'boolean', value: true };
        const res = await authFetch(`${base}/products/${product.id || product.shopify_id}/metafields/upsert/`, {
          method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error('Failed to set preorder');
      }
      // reload metafields
      const mRes = await authFetch(`${base}/products/${id}/metafields/`);
      const mData = await mRes.json();
      setMetafields(mData.metafields||[]);
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="pd-container">
      <div className="pd-header">
        <div className="pd-breadcrumbs"><Link to="/products">← Back to Products</Link></div>
        <h1 className="pd-title">
          <input
            className="pd-input"
            defaultValue={product.title}
            onChange={(e)=>setEdit(v=>({...v, title:e.target.value}))}
          />
        </h1>
        <div className="pd-sub">{product.vendor || '—'} • {product.product_type || '—'} <span className={`badge ${product.status==='active'?'success':'muted'}`}>{product.status || '—'}</span>
          <span className={`badge ${preorderOn?'warning':'muted'}`} style={{ marginLeft: 8 }}>preorder: {preorderOn ? 'true' : 'false'}</span>
        </div>
        <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn pd-btn pd-btn-primary" disabled={saving} onClick={saveChanges}>{saving?'Saving…':'Save changes'}</button>
          <button className="btn pd-btn pd-btn-secondary" onClick={togglePreorder}>{preorderOn ? 'Disable Preorder' : 'Enable Preorder'}</button>
          {error && <span className="pd-error" style={{ marginLeft: 8 }}>{error}</span>}
        </div>
      </div>

      <div className="pd-grid">
        <div className="pd-media">
          {cover ? (
            <img src={cover} alt={product.title} />
          ) : (
            <div className="skeleton" style={{ width: '100%', height: 320, borderRadius: 12 }} />
          )}
          {product.images?.length > 1 && (
            <div className="pd-thumbs">
              {product.images.slice(0, 12).map((img) => (
                <img key={img.id || img.src} src={img.src} alt="thumb" onClick={()=>selectImage(img)} style={{ cursor:'pointer', outline: product.image?.id===img.id? '2px solid #4f46e5':'none' }} />
              ))}
            </div>
          )}
        </div>

        <div className="pd-info">
          <div className="card">
            <div className="card-title">Overview</div>
            <div className="kv-grid">
              <KeyValue label="Handle" value={product.handle} />
              <div className="kv">
                <div className="kv-label">Status</div>
                <select className="kv-value" defaultValue={product.status} onChange={(e)=>setEdit(v=>({...v, status:e.target.value}))}>
                  <option value="active">active</option>
                  <option value="draft">draft</option>
                  <option value="archived">archived</option>
                </select>
              </div>
              <div className="kv">
                <div className="kv-label">Tags</div>
                <input className="kv-value" defaultValue={product.tags || ''} onChange={(e)=>setEdit(v=>({...v, tags:e.target.value}))} />
              </div>
              <div className="kv">
                <div className="kv-label">Vendor</div>
                <input className="kv-value" defaultValue={product.vendor || ''} onChange={(e)=>setEdit(v=>({...v, vendor:e.target.value}))} />
              </div>
              <div className="kv">
                <div className="kv-label">Product Type</div>
                <input className="kv-value" defaultValue={product.product_type || ''} onChange={(e)=>setEdit(v=>({...v, product_type:e.target.value}))} />
              </div>
              <KeyValue label="Created" value={(product.created_at || '').toString().slice(0,10)} />
              <KeyValue label="Updated" value={(product.updated_at || '').toString().slice(0,10)} />
            </div>
          </div>

          <div className="card">
            <div className="card-title" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span>Description</span>
            </div>
            <div contentEditable className="pd-desc card" style={{ background:'#fafafa' }}
              suppressContentEditableWarning={true}
              onInput={(e)=>setEdit(v=>({...v, body_html: e.currentTarget.innerHTML}))}
              dangerouslySetInnerHTML={{ __html: (edit.body_html ?? product.body_html) || '<em>No description</em>' }} />
          </div>

          <div className="card">
            <div className="card-title">Variants ({product.variants?.length || 0})</div>
            <div className="table">
              <div className="tr th" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
                <div>Title</div>
                <div>SKU</div>
                <div>Price</div>
                <div>Compare-at</div>
                <div>Requires Shipping</div>
              </div>
              {(product.variants || []).map(v => (
                <div className="tr" key={v.id} style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
                  <div><input defaultValue={v.title} onChange={(e)=>{ v._edit = { ...(v._edit||{}), title: e.target.value }; }} /></div>
                  <div><input defaultValue={v.sku || ''} onChange={(e)=>{ v._edit = { ...(v._edit||{}), sku: e.target.value }; }} /></div>
                  <div><input defaultValue={v.price || ''} onChange={(e)=>{ v._edit = { ...(v._edit||{}), price: e.target.value }; }} /></div>
                  <div><input defaultValue={v.compare_at_price || ''} onChange={(e)=>{ v._edit = { ...(v._edit||{}), compare_at_price: e.target.value }; }} /></div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input type="checkbox" defaultChecked={!!v.requires_shipping} onChange={(e)=>{ v._edit = { ...(v._edit||{}), requires_shipping: e.target.checked }; }} />
                    <button className="btn" onClick={async()=>{
                      try {
                        const res = await authFetch(`${base}/products/${product.id || product.shopify_id}/variants/${v.id}/`, { method:'PUT', headers:{'Content-Type':'application/json'}, body: JSON.stringify(v._edit||{}) });
                        if (!res.ok) throw new Error('Variant save failed');
                      } catch(err) { alert(err.message); }
                    }}>Save</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-title">Metafields ({metafields.length})</div>
            <div className="mf-list">
              {metafields.map(m => {
                const def = definitions.find(d => d.namespace === m.namespace && d.key === m.key);
                const hasOptions = Array.isArray(def?.possible_values) && def.possible_values.length > 0;
                const inputType = def?.type || m.type;
                const renderValueInput = () => {
                  // Dropdown (select)
                  if (hasOptions) {
                    return (
                      <select defaultValue={String(m.value)} onChange={(e)=>{ m._edit = { ...(m._edit||{}), value: e.target.value }; }}>
                        {def.possible_values.map(opt => (
                          <option key={String(opt)} value={String(opt)}>{String(opt)}</option>
                        ))}
                      </select>
                    );
                  }
                  // Boolean
                  if (inputType === 'boolean') {
                    const checked = String(m.value).toLowerCase() === 'true' || m.value === true;
                    return (
                      <label style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                        <input type="checkbox" defaultChecked={checked} onChange={(e)=>{ m._edit = { ...(m._edit||{}), value: e.target.checked }; }} />
                        <span>{checked ? 'true' : 'false'}</span>
                      </label>
                    );
                  }
                  // Number
                  if (['number_integer','number_decimal'].includes(inputType) || /^number/.test(inputType)) {
                    return <input type="number" defaultValue={String(m.value)} onChange={(e)=>{ m._edit = { ...(m._edit||{}), value: e.target.value }; }} />;
                  }
                  // Default text/JSON
                  return <textarea className="code-area" defaultValue={String(m.value)} onChange={(e)=>{ m._edit = { ...(m._edit||{}), value: e.target.value }; }} />;
                };
                return (
                  <div className="mf-row" key={m.id}>
                    <div className="mf-head">
                      <span className="badge ns">{m.namespace}</span>
                      <span className="badge key">{m.key}</span>
                    </div>
                    {renderValueInput()}
                  <div className="mf-actions">
                    <button className="btn" onClick={async()=>{
                      try { const res = await authFetch(`${base}/metafields/${m.id}/`, { method:'PUT', headers:{'Content-Type':'application/json'}, body: JSON.stringify(m._edit||{}) }); if(!res.ok) throw new Error('Update failed'); } catch(err) { alert(err.message); }
                    }}>Save</button>
                    <button className="btn" onClick={async()=>{
                      try { const res = await authFetch(`${base}/metafields/${m.id}/delete/`, { method:'DELETE' }); if(!res.ok) throw new Error('Delete failed'); setMetafields(prev=>prev.filter(x=>x.id!==m.id)); } catch(err) { alert(err.message); }
                    }}>Delete</button>
                  </div>
                  </div>
                );
              })}
              <div className="mf-row">
                <div className="mf-head">
                  <input placeholder="namespace" onChange={(e)=>{ (ProductDetail._newMF = ProductDetail._newMF || {}).namespace = e.target.value; }} />
                  <input placeholder="key" onChange={(e)=>{ (ProductDetail._newMF = ProductDetail._newMF || {}).key = e.target.value; }} />
                  <input placeholder="type (e.g., single_line_text_field)" onChange={(e)=>{ (ProductDetail._newMF = ProductDetail._newMF || {}).type = e.target.value; }} />
                </div>
                <textarea className="code-area" placeholder="value" onChange={(e)=>{ (ProductDetail._newMF = ProductDetail._newMF || {}).value = e.target.value; }} />
                <div className="mf-actions">
                  <button className="btn" onClick={async()=>{
                    try {
                      const body = ProductDetail._newMF || {};
                      const res = await authFetch(`${base}/products/${product.id || product.shopify_id}/metafields/upsert/`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(body) });
                      if (!res.ok) throw new Error('Metafield create failed');
                      const mRes = await authFetch(`${base}/products/${id}/metafields/`); const mData = await mRes.json(); setMetafields(mData.metafields||[]);
                    } catch(err) { alert(err.message); }
                  }}>Add</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;

